import type { CellUpdate, SessionUpdate, FamiliesUpdate, OccupancySubscription } from '@features/workshop-plan/types';

import { useEffect } from 'react';
import { EventSource } from 'eventsource';

import { baseApi } from '@core/api/base-api';
import { useAppDispatch } from '@core/store/hooks';

import { applyCellUpdate } from '@features/workshop-plan/utils/plan-cell';
import { workshopPlanApi, useLazyGetOccupancySubscriptionQuery } from '@features/workshop-plan/api/workshop-plan-api';

const RENEW_MARGIN_MS = 60_000;
const RETRY_DELAY_MS = 10_000;
/** Event type of the messages carrying the up to date products to customize per family. */
const FAMILIES_EVENT = 'families';
/** Event type of the messages announcing that a customization session was created or changed. */
const SESSION_EVENT = 'session';

/** Mercure 1.0 selects topics with `match` and wants the subscriber token in an Authorization header. */
const buildHubUrl = ({ url, topics }: OccupancySubscription): string => {
  const hub = new URL(url, window.location.origin);
  topics.forEach((topic) => hub.searchParams.append('match', topic));
  return hub.toString();
};

/**
 * Keeps the workshop plan up to date: each cell update published on the hub is applied to the cached plan,
 * the list of the products to customize per family is replaced by each snapshot published, and a changed
 * customization session invalidates what the clients read about it (list, detail, creation options).
 * It is mounted once for the whole workshop, so every page of it stays up to date.
 * The stream is reopened with a fresh token before the current one expires, and after a failure.
 */
export const useWorkshopOccupancy = (): void => {
  const dispatch = useAppDispatch();
  const [fetchSubscription] = useLazyGetOccupancySubscriptionQuery();

  useEffect(() => {
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const onMessage = (event: MessageEvent<string>) => {
      const update = JSON.parse(event.data) as CellUpdate;
      dispatch(
        workshopPlanApi.util.updateQueryData('getWorkshopPlans', undefined, (plans) => applyCellUpdate(plans, update))
      );
      // A cell changed: the products to customize and the boxes ready to leave the workshop may have changed too
      dispatch(baseApi.util.invalidateTags(['familyDemand', 'workshopReleasable']));
    };

    const onFamiliesChanged = (event: MessageEvent<string>) => {
      const { families } = JSON.parse(event.data) as FamiliesUpdate;
      dispatch(workshopPlanApi.util.updateQueryData('getFamilyDemand', undefined, () => families));
    };

    const onSessionChanged = (event: MessageEvent<string>) => {
      const { id } = JSON.parse(event.data) as SessionUpdate;
      dispatch(
        baseApi.util.invalidateTags([
          'customizationSessionList',
          'customizationSessionOptions',
          { type: 'customizationSession', id },
        ])
      );
    };

    const connect = async () => {
      try {
        const subscription = await fetchSubscription().unwrap();
        if (stopped) return;

        source?.close();
        const current = new EventSource(buildHubUrl(subscription), {
          fetch: (input, init) =>
            fetch(input, { ...init, headers: { ...init.headers, Authorization: `Bearer ${subscription.token}` } }),
        });
        source = current;
        current.onmessage = onMessage;
        current.addEventListener(FAMILIES_EVENT, onFamiliesChanged);
        current.addEventListener(SESSION_EVENT, onSessionChanged);
        // The library retries a dropped connection itself; it gives up on a refused one (expired token).
        current.onerror = () => {
          if (current.readyState !== EventSource.CLOSED || stopped) return;
          clearTimeout(timer);
          timer = setTimeout(connect, RETRY_DELAY_MS);
        };

        const lifetime = new Date(subscription.expiresAt).getTime() - Date.now() - RENEW_MARGIN_MS;
        timer = setTimeout(connect, Math.max(lifetime, RETRY_DELAY_MS));
      } catch {
        if (!stopped) timer = setTimeout(connect, RETRY_DELAY_MS);
      }
    };

    connect();

    return () => {
      stopped = true;
      clearTimeout(timer);
      source?.close();
    };
  }, [dispatch, fetchSubscription]);
};
